import { Fragment, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import Loading from "components/Loading";
import { useState, createContext } from 'react';
import Footer from "components/Footer/Footer";
import BismillahBanner from "components/BismillahBanner/BismillahBanner";
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import WishList from 'pages/Wishlist/Wishlist';
import Cart from 'pages/Cart/Cart';
import AddToCart from 'pages/AddToCart/AddToCart';
import Product from 'pages/Product/Product';
import ProductService from './services/products';
import CartService from './services/cart';
import WishlistService from './services/wishlist';
import AuthService from './services/auth';
import AdminRoute from 'components/AdminRoute';
import VendorRoute from 'components/VendorRoute';
import VendorLayout from 'components/Vendor/VendorLayout';
import Chatbot from 'components/Chatbot/Chatbot';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import AdminShell from 'components/Admin/AdminShell';
import { notifyAppReady } from './utils/appPreloader';
import AuthSyncNotice from './components/AuthSyncNotice';
import ErrorBoundary from './components/ErrorBoundary';
import { startAuthSync } from './services/authSync';
import { APP_TOAST_EVENT } from './services/authEvents';
import { isAdminWorkspace, isStorefrontChromeHidden } from './utils/adminPaths';
import { applyDocumentLanguage, getPreferredLanguage, setLanguage, subscribeLanguage, t } from './i18n';
import { StoreModeContext } from './context/StoreModeContext';
import LegacyHandleRedirect from 'components/Store/LegacyHandleRedirect';

// Admin console + info pages: loaded on first visit (shoppers never download them).
const AdminDashboard = lazy(() => import('pages/Admin/AdminDashboard'));
const AdminLogin = lazy(() => import('pages/Admin/AdminLogin'));
const ProductManagement = lazy(() => import('pages/Admin/ProductManagement'));
const OrderManagement = lazy(() => import('pages/Admin/OrderManagement'));
const SubscriptionManagement = lazy(() => import('pages/Admin/SubscriptionManagement'));
const UserManagement = lazy(() => import('pages/Admin/UserManagement'));
const FlaggedReviews = lazy(() => import('pages/Admin/FlaggedReviews'));
const NewsletterManagement = lazy(() => import('pages/Admin/NewsletterManagement'));
const VendorApplications = lazy(() => import('pages/Admin/VendorApplications'));
const VendorApplicationDetail = lazy(() => import('pages/Admin/VendorApplicationDetail'));
const AdminStores = lazy(() => import('pages/Admin/AdminStores'));
const AdminStoreDetail = lazy(() => import('pages/Admin/AdminStoreDetail'));
const AdminPlans = lazy(() => import('pages/Admin/AdminPlans'));
const AdminBilling = lazy(() => import('pages/Admin/AdminBilling'));
const AdminEarnings = lazy(() => import('pages/Admin/AdminEarnings'));
const AdminCommission = lazy(() => import('pages/Admin/AdminCommission'));
const AdminModeration = lazy(() => import('pages/Admin/AdminModeration'));
const AdminTaxonomy = lazy(() => import('pages/Admin/AdminTaxonomy'));
const AdminAuditLog = lazy(() => import('pages/Admin/AdminAuditLog'));
const AboutUs = lazy(() => import('pages/AboutUs/AboutUs'));
const ContactUs = lazy(() => import('pages/ContactUs/ContactUs'));
const Home = lazy(() => import('./pages/Home/Home'));
const Navbar = lazy(() => import('./components/Navbar/Navbar'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation/OrderConfirmation'));
const Shop = lazy(() => import('./pages/Shop/Shop'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const Account = lazy(() => import('./pages/Account/Account'));
const VendorApplication = lazy(() => import('./pages/VendorApplication/VendorApplication'));
const Orders = lazy(() => import('./pages/Orders/Orders'));
const OrderDetail = lazy(() => import('./pages/Orders/OrderDetail'));
const TrackOrder = lazy(() => import('./pages/Track/TrackOrder'));
const ConfirmOrder = lazy(() => import('./pages/ConfirmOrder/ConfirmOrder'));
const OrderInvoicePage = lazy(() => import('./pages/Invoice/InvoicePages').then((m) => ({ default: m.OrderInvoicePage })));
const VendorInvoicePage = lazy(() => import('./pages/Invoice/InvoicePages').then((m) => ({ default: m.VendorInvoicePage })));
const AdminInvoicesPrintPage = lazy(() => import('./pages/Invoice/InvoicePages').then((m) => ({ default: m.AdminInvoicesPrintPage })));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const SetPassword = lazy(() => import('./pages/Auth/SetPassword'));
const VerifyOtp = lazy(() => import('./pages/Auth/VerifyOtp'));
const SlidingAuth = lazy(() => import('./pages/Auth/SlidingAuth'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService/TermsOfService'));
const DataDeletion = lazy(() => import('./pages/DataDeletion/DataDeletion'));
const NewsletterUnsubscribe = lazy(() => import('./pages/Newsletter/NewsletterUnsubscribe'));
const OurServicesPage = lazy(() => import('./pages/Help/OurServicesPage'));
const HowToShopPage = lazy(() => import('./pages/Help/HowToShopPage'));
const FAQPage = lazy(() => import('./pages/Help/FAQPage'));
const PaymentMethodsPage = lazy(() => import('./pages/Help/PaymentMethodsPage'));
const MoneyBackPage = lazy(() => import('./pages/Help/MoneyBackPage'));
const ReturnsPage = lazy(() => import('./pages/Help/ReturnsPage'));
const ShippingPage = lazy(() => import('./pages/Help/ShippingPage'));
const DummyJsonCatalog = lazy(() => import('./pages/Catalog/DummyJsonCatalog'));
// R5 reviews + Zaynic Coins (lazy: none of these are on the home/shop path).
const MyCoins = lazy(() => import('./pages/Coins/MyCoins'));
const CoinsTerms = lazy(() => import('./pages/Coins/CoinsTerms'));
const MyDownloads = lazy(() => import('./pages/Downloads/MyDownloads'));
const VendorReviews = lazy(() => import('./pages/Vendor/VendorReviews'));
const VendorDeals = lazy(() => import('./pages/Vendor/VendorDeals'));
const AdminDeals = lazy(() => import('./pages/Admin/AdminDeals'));
const AdminReviewQueue = lazy(() => import('./pages/Admin/AdminReviewQueue'));
const AdminLoyalty = lazy(() => import('./pages/Admin/AdminLoyalty'));
// R6 category experience modules
const BatchPage = lazy(() => import('./pages/Batch/BatchPage'));
const VendorSizeCharts = lazy(() => import('./pages/Vendor/VendorSizeCharts'));
const DummyJsonProductDetail = lazy(() => import('./pages/Catalog/DummyJsonProductDetail'));
const VendorOverview = lazy(() => import('./pages/Vendor/VendorOverview'));
const VendorProducts = lazy(() => import('./pages/Vendor/VendorProducts'));
const VendorProductForm = lazy(() => import('./pages/Vendor/VendorProductForm'));
const VendorInventory = lazy(() => import('./pages/Vendor/VendorInventory'));
const VendorOrders = lazy(() => import('./pages/Vendor/VendorOrders'));
const VendorAnalytics = lazy(() => import('./pages/Vendor/VendorAnalytics'));
const VendorStoreSettings = lazy(() => import('./pages/Vendor/VendorStoreSettings'));
const VendorBilling = lazy(() => import('./pages/Vendor/VendorBilling'));
const VendorStatement = lazy(() => import('./pages/Vendor/VendorStatement'));
const VendorTaxonomyRequest = lazy(() => import('./pages/Vendor/VendorTaxonomyRequest'));
// Store mode: /<handle> and /<handle>/<product-slug> (routes added LAST).
const StorePage = lazy(() => import('./pages/Store/StorePage'));
const StoreProductPage = lazy(() => import('./pages/Store/StoreProductPage'));
const StoreModeBar = lazy(() => import('./components/Store/StoreModeBar'));
const StoreModeFooter = lazy(() => import('./components/Store/StoreModeFooter'));
export const DataContext = createContext();

const SEVERITY_STYLES = {
  success: "bg-emerald-700 text-white ring-emerald-800/40",
  error: "bg-rose-700 text-white ring-rose-900/30",
  warning: "bg-amber-600 text-white ring-amber-800/40",
  info: "bg-sky-700 text-white ring-sky-900/30",
};

function App() {
  const location = useLocation();
  const hideStorefrontChrome = isStorefrontChromeHidden(location.pathname);
  const showAdminShell = isAdminWorkspace(location.pathname);

  // R9: the visitor's EN / Urdu choice. Admin pages always render English (and LTR).
  // setLanguage() runs during render so every t() below reads the right catalog;
  // the page tree is keyed by language, so a switch re-renders everything at once.
  const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage);
  useEffect(() => subscribeLanguage(setPreferredLanguage), []);
  // setLanguage() returns the language in effect (English if a catalog failed to load).
  const language = setLanguage(showAdminShell || location.pathname === '/admin-login' ? 'en' : preferredLanguage);
  useLayoutEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  // Store mode: the vendor store whose own page is open (null elsewhere).
  const [storeModeStore, setStoreModeStore] = useState(null);
  const storeModeValue = useMemo(() => ({ store: storeModeStore, setStore: setStoreModeStore }), [storeModeStore]);
  const storeMode = storeModeStore && !hideStorefrontChrome ? storeModeStore : null;

  const [products, setProducts] = useState([]);
  const [logos, setLogos] = useState([]);
  const [wishList, setWishList] = useState([]);
  const [cartList, setCartList] = useState([]);
  const [snackbar, setSnackbarState] = useState({
    message: "",
    open: false,
    severity: ""
  });

  // One deduplicated error toast (R0): many widgets failing with the same
  // error (e.g. a 403 burst) show it once instead of flickering repeatedly.
  const lastErrorToast = useRef({ message: null, at: 0 });
  const setSnackbar = useCallback((next) => {
    if (next && typeof next === 'object' && next.open && next.severity === 'error') {
      const now = Date.now();
      if (lastErrorToast.current.message === next.message && now - lastErrorToast.current.at < 5000) return;
      lastErrorToast.current = { message: next.message, at: now };
    }
    setSnackbarState(next);
  }, []);

  // Cross-tab auth sync + /users/me reloads (R0); app-level toasts.
  useEffect(() => {
    startAuthSync();
    const onToast = (e) => {
      const { message, severity = 'info' } = e.detail || {};
      if (message) setSnackbar({ message, severity, open: true, duration: 5000 });
    };
    window.addEventListener(APP_TOAST_EVENT, onToast);
    return () => window.removeEventListener(APP_TOAST_EVENT, onToast);
  }, [setSnackbar]);

  // Initialize app data (non-blocking — logo preloader hides via notifyAppReady)
  useEffect(() => {
    let cancelled = false;
    const INIT_TIMEOUT_MS = 8000;

    const initializeApp = async () => {
      try {
        // Try to fetch products from backend, if it fails use sample data
        try {
          const productsResult = await ProductService.getProducts({ limit: 100 });
          let items = productsResult.success ? productsResult.products?.items : null;
          // [] is truthy in JS — empty cached payloads used to skip samples & stay blank forever.
          if (!Array.isArray(items) || items.length === 0) {
            const retry = await ProductService.getProducts({
              limit: 100,
              _t: Date.now(),
            });
            if (retry.success && Array.isArray(retry.products?.items) && retry.products.items.length) {
              items = retry.products.items;
            }
          }
          if (productsResult.success && Array.isArray(items) && items.length > 0) {
            setProducts(items);
          } else {
            throw new Error('Failed to fetch products from backend');
          }
        } catch (apiError) {
          // debug log removed for clean builds
          // Use sample products if backend is not available
          // i18n-ignore-start: offline sample catalogue data (product names are never translated)
          setProducts([
            {
              id: 1,
              name: "iPhone 15 Pro",
              description: "The most advanced iPhone with titanium design and A17 Pro chip.",
              price: 299999, // PKR mein real price format
              images: [
                "https://images.unsplash.com/photo-1695048132703-4f4ea4c7a4dd?auto=format&fit=crop&w=800&q=80"
              ],
              category: "Smartphones",
              brand: "Apple",
              is_featured: true,
              is_trending: true,
              is_top_selling: true,
              rating: 4.8,
              review_count: 124
            },
            {
              id: 2,
              name: "MacBook Air M3",
              description: "Supercharged by the M3 chip for incredible performance and battery life.",
              price: 320000,
              images: [
                "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=800&q=80"
              ],
              category: "Computers",
              brand: "Apple",
              is_featured: true,
              is_trending: false,
              is_top_selling: true,
              rating: 4.9,
              review_count: 89
            },
            {
              id: 3,
              name: "Samsung Galaxy S24 Ultra",
              description: "Ultimate smartphone with S Pen, AI features, and 200MP camera.",
              price: 350000,
              images: [
                "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?auto=format&fit=crop&w=800&q=80"
              ],
              category: "Smartphones",
              brand: "Samsung",
              is_featured: false,
              is_trending: true,
              is_top_selling: true,
              rating: 4.7,
              review_count: 156
            },
            {
              id: 4,
              name: "Sony WH-1000XM5",
              description: "Industry-leading noise canceling wireless headphones.",
              price: 85000,
              images: [
                "https://images.unsplash.com/photo-1618366712010-f4ae9c647dcb?auto=format&fit=crop&w=800&q=80"
              ],
              category: "Accessories",
              brand: "Sony",
              is_featured: true,
              is_trending: true,
              is_top_selling: false,
              rating: 4.9,
              review_count: 342
            },
            {
              id: 5,
              name: "PlayStation 5 Console",
              description: "Experience lightning-fast loading and ultra-high-speed SSD.",
              price: 155000,
              images: [
                "https://images.unsplash.com/photo-1606813907291-d86efa9b94db?auto=format&fit=crop&w=800&q=80"
              ],
              category: "Gaming",
              brand: "Sony",
              is_featured: false,
              is_trending: true,
              is_top_selling: true,
              rating: 4.8,
              review_count: 890
            },
            {
              id: 6,
              name: "Apple Watch Series 9",
              description: "Advanced health tracking smartwatch with always-on retina display.",
              price: 120000,
              images: [
                "https://images.unsplash.com/photo-1434493789847-2f02dc6ca35d?auto=format&fit=crop&w=800&q=80"
              ],
              category: "Wearables",
              brand: "Apple",
              is_featured: true,
              is_trending: false,
              is_top_selling: false,
              rating: 4.6,
              review_count: 210
            }
          ]);
        }

        // Initialize cart and wishlist
        const localCart = CartService.loadLocalCart();

        if (AuthService.isAuthenticated()) {
          try {
            const mergeResult = await CartService.mergeLocalCartToBackend();
            if (!mergeResult.success) {
              console.warn('Cart merge failed:', mergeResult.error);
            }
          } catch (error) {
            console.error('Error merging local cart on load:', error);
          }

          try {
            const cartResult = await CartService.getCart();
            setCartList(cartResult.success ? cartResult.cartItems : []);
          } catch (error) {
            console.error('Error loading backend cart:', error);
            setCartList([]);
          }

          try {
            const wishlistResult = await WishlistService.getWishlist();
            if (wishlistResult.success) {
              setWishList(wishlistResult.wishlistItems);
            } else {
              setWishList([]);
            }
          } catch (error) {
            console.error('Error loading wishlist:', error);
            setWishList([]);
          }
        } else {
          setCartList(localCart);
          const localWishlist = WishlistService.loadLocalWishlist();
          setWishList(localWishlist);
        }

        // Set some sample logos
        setLogos([
          { id: 1, name: 'Company 1', logo: '/images/logo1.svg' },
          { id: 2, name: 'Company 2', logo: '/images/logo2.svg' },
        ]);
        // i18n-ignore-end

      } catch (error) {
        console.error('Error initializing app:', error);
        setSnackbar({
          message: t('common.appLoadFailed'),
          open: true,
          severity: "error"
        });
      } finally {
        if (!cancelled) notifyAppReady();
      }
    };

    const timeoutId = window.setTimeout(() => {
      if (!cancelled) notifyAppReady();
    }, INIT_TIMEOUT_MS);

    initializeApp().finally(() => {
      window.clearTimeout(timeoutId);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [setSnackbar]);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbar(prev => ({ ...prev, open: false }));
  }, [setSnackbar]);

  const showSnackbar = useCallback(({ message, severity = 'info', action = null, duration = 2500 }) => {
    setSnackbar({ message, severity, action, open: true, duration });
  }, [setSnackbar]);

  const toast = {
    success: (message, opts = {}) => showSnackbar({ message, severity: 'success', ...opts }),
    error: (message, opts = {}) => showSnackbar({ message, severity: 'error', ...opts }),
    info: (message, opts = {}) => showSnackbar({ message, severity: 'info', ...opts }),
    warning: (message, opts = {}) => showSnackbar({ message, severity: 'warning', ...opts }),
  };

  useEffect(() => {
    if (!snackbar.open) return undefined;
    const ms = snackbar.duration != null ? snackbar.duration : 3000;
    const id = window.setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, ms);
    return () => window.clearTimeout(id);
  }, [snackbar.open, snackbar.duration, snackbar.message, setSnackbar]);

  useEffect(() => {
    if (!snackbar.open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        handleClose(e, 'escapeKeyDown');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snackbar.open, handleClose]);

  useEffect(() => {
    if (!AuthService.isAuthenticated()) {
      WishlistService.saveLocalWishlist(wishList);
    }
  }, [wishList]);

  useEffect(() => {
    if (!AuthService.isAuthenticated()) {
      try {
        CartService.saveLocalCart(cartList);
      } catch (error) {
        console.error('Error saving cart to local storage:', error);
      }
    }
  }, [cartList]);

  const severityKey = snackbar.severity && SEVERITY_STYLES[snackbar.severity]
    ? snackbar.severity
    : 'info';
  const barTone = SEVERITY_STYLES[severityKey] || SEVERITY_STYLES.info;

  return (
    <DataContext.Provider value={{ products, setProducts, logos, setLogos, wishList, setWishList, cartList, setCartList, snackbar, setSnackbar, showSnackbar, toast }}>
      <StoreModeContext.Provider value={storeModeValue}>
      <Suspense fallback={<Loading />}>
        <Fragment key={language}>
        <ScrollToTop />
        {/* `contents` wrappers: no box on screen; hidden when printing (R3 invoices/reports). */}
        <div className="contents print:hidden">
          {showAdminShell && <AdminShell />}
          {!hideStorefrontChrome && <BismillahBanner />}
          {!hideStorefrontChrome && (storeMode ? <StoreModeBar store={storeMode} /> : <Navbar />)}
        </div>
        <Routes>
          <Route path='/' element={<Home />} />
          <Route path='/about-us' element={<AboutUs />} />
          <Route path='/services' element={<OurServicesPage />} />
          <Route path='/how-to-shop' element={<HowToShopPage />} />
          <Route path='/faq' element={<FAQPage />} />
          <Route path='/contact-us' element={<ContactUs />} />
          <Route path='/payment-methods' element={<PaymentMethodsPage />} />
          <Route path='/money-back-guarantee' element={<MoneyBackPage />} />
          <Route path='/returns' element={<ReturnsPage />} />
          <Route path='/shipping' element={<ShippingPage />} />
          <Route path='/wishlist' element={<WishList />} />
          <Route path='/cart' element={<Cart />} />
          <Route path='/checkout' element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path='/add-to-cart' element={<ProtectedRoute><AddToCart /></ProtectedRoute>} />
          <Route path='/order-confirmation/:orderId' element={<OrderConfirmation />} />
          <Route path='/product/:productID' element={<Product />} />
          <Route path='/product' element={<Navigate to={"/shop"} />} />
          <Route path='/shop' element={<Shop />} />
          <Route path='/catalog/dummyjson' element={<DummyJsonCatalog />} />
          <Route path='/catalog/dummyjson/:productId' element={<DummyJsonProductDetail />} />
          <Route path='/account' element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path='/sell' element={<ProtectedRoute><VendorApplication /></ProtectedRoute>} />
          <Route path='/vendor' element={<VendorRoute><VendorLayout><VendorOverview /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/products' element={<VendorRoute><VendorLayout><VendorProducts /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/products/new' element={<VendorRoute><VendorLayout><VendorProductForm /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/products/:id/edit' element={<VendorRoute><VendorLayout><VendorProductForm /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/inventory' element={<VendorRoute><VendorLayout><VendorInventory /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/orders' element={<VendorRoute><VendorLayout><VendorOrders /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/analytics' element={<VendorRoute><VendorLayout><VendorAnalytics /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/store' element={<VendorRoute><VendorLayout><VendorStoreSettings /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/billing' element={<VendorRoute><VendorLayout><VendorBilling /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/statement' element={<VendorRoute><VendorLayout><VendorStatement /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/taxonomy-requests' element={<VendorRoute><VendorLayout><VendorTaxonomyRequest /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/reviews' element={<VendorRoute><VendorLayout><VendorReviews /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/deals' element={<VendorRoute><VendorLayout><VendorDeals /></VendorLayout></VendorRoute>} />
          <Route path='/vendor/size-charts' element={<VendorRoute><VendorLayout><VendorSizeCharts /></VendorLayout></VendorRoute>} />
          <Route path='/batch/:code' element={<BatchPage />} />
          <Route path='/coins' element={<ProtectedRoute><MyCoins /></ProtectedRoute>} />
          <Route path='/downloads' element={<ProtectedRoute><MyDownloads /></ProtectedRoute>} />
          <Route path='/coins/terms' element={<CoinsTerms />} />
          <Route path='/orders' element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path='/orders/:id' element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
          <Route path='/orders/:id/invoice' element={<ProtectedRoute><OrderInvoicePage /></ProtectedRoute>} />
          <Route path='/track' element={<TrackOrder />} />
          <Route path='/confirm-order' element={<ConfirmOrder />} />
          <Route path='/vendor/orders/:voId/invoice' element={<VendorRoute><VendorInvoicePage /></VendorRoute>} />
          <Route path='/subscriptions' element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
          <Route path='/forgot-password' element={<ForgotPassword />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='/set-password' element={<SetPassword />} />
          <Route path='/verify-otp' element={<VerifyOtp />} />
          <Route path='/newsletter/unsubscribe' element={<NewsletterUnsubscribe />} />
          <Route path='/auth/sliding' element={<SlidingAuth />} />
          <Route path='/privacy-policy' element={<PrivacyPolicy />} />
          <Route path='/data-deletion' element={<DataDeletion />} />
          <Route path='/userdeletion' element={<DataDeletion />} />
          <Route path='/terms' element={<TermsOfService />} />
          <Route path='/terms-of-service' element={<TermsOfService />} />
          <Route path='/admin' element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path='/admin-login' element={<AdminLogin />} />
          <Route path='/admin/products' element={<AdminRoute><ProductManagement /></AdminRoute>} />
          <Route path='/admin/products/new' element={<AdminRoute><ProductManagement /></AdminRoute>} />
          <Route path='/admin/orders' element={<AdminRoute><OrderManagement /></AdminRoute>} />
          <Route path='/admin/orders/print' element={<AdminRoute><AdminInvoicesPrintPage /></AdminRoute>} />
          <Route path='/admin/users' element={<AdminRoute><UserManagement /></AdminRoute>} />
          <Route path='/admin/subscriptions' element={<AdminRoute><SubscriptionManagement /></AdminRoute>} />
          <Route path='/admin/reviews' element={<AdminRoute><FlaggedReviews /></AdminRoute>} />
          <Route path='/admin/newsletter' element={<AdminRoute><NewsletterManagement /></AdminRoute>} />
          <Route path='/admin/vendor-applications' element={<AdminRoute><VendorApplications /></AdminRoute>} />
          <Route path='/admin/vendor-applications/:id' element={<AdminRoute><VendorApplicationDetail /></AdminRoute>} />
          <Route path='/admin/stores' element={<AdminRoute><AdminStores /></AdminRoute>} />
          <Route path='/admin/stores/:id' element={<AdminRoute><AdminStoreDetail /></AdminRoute>} />
          <Route path='/admin/plans' element={<AdminRoute><AdminPlans /></AdminRoute>} />
          <Route path='/admin/billing' element={<AdminRoute><AdminBilling /></AdminRoute>} />
          <Route path='/admin/earnings' element={<AdminRoute><AdminEarnings /></AdminRoute>} />
          <Route path='/admin/commission' element={<AdminRoute><AdminCommission /></AdminRoute>} />
          <Route path='/admin/moderation' element={<AdminRoute><AdminModeration /></AdminRoute>} />
          <Route path='/admin/taxonomy' element={<AdminRoute><AdminTaxonomy /></AdminRoute>} />
          <Route path='/admin/audit-log' element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
          <Route path='/admin/review-queue' element={<AdminRoute><AdminReviewQueue /></AdminRoute>} />
          <Route path='/admin/loyalty' element={<AdminRoute><AdminLoyalty /></AdminRoute>} />
          <Route path='/admin/deals' element={<AdminRoute><AdminDeals /></AdminRoute>} />
          {/* Store mode -- keep these LAST: every static route above outranks them, and a
              reserved word (src/utils/reservedHandles.json) or unknown store renders NotFound. */}
          <Route path='/:storeHandle' element={<LegacyHandleRedirect><StorePage /></LegacyHandleRedirect>} />
          <Route path='/:storeHandle/:productSlug' element={<LegacyHandleRedirect><StoreProductPage /></LegacyHandleRedirect>} />
          <Route path='*' element={<NotFound />} />
        </Routes>
        <div className="contents print:hidden">
          {!hideStorefrontChrome && <ErrorBoundary fallback={null}><Chatbot /></ErrorBoundary>}
          <AuthSyncNotice />
          {!hideStorefrontChrome && (storeMode ? <StoreModeFooter store={storeMode} /> : <Footer />)}
        </div>

        {/* Global toast — native + Tailwind (replaces MUI Snackbar / Alert / Slide) */}
        <div
          className={[
            'pointer-events-none fixed bottom-4 end-4 z-[1400] flex w-[min(100vw-2rem,28rem)] max-w-[min(100vw-2rem,28rem)] justify-end motion-reduce:transition-none',
            'transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
            snackbar.open ? 'translate-x-0 opacity-100' : 'translate-x-[calc(100%+1.5rem)] rtl:-translate-x-[calc(100%+1.5rem)] opacity-0',
          ].join(' ')}
          aria-hidden={!snackbar.open}
        >
          <div
            role="alert"
            aria-live="polite"
            aria-hidden={!snackbar.open}
            className={[
              'pointer-events-auto flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium shadow-[0_10px_40px_rgba(15,23,42,0.18)] ring-1 ring-inset',
              barTone,
            ].join(' ')}
          >
            <p className="min-w-0 flex-1 leading-snug">{snackbar.message}</p>
            {snackbar.action ? (
              <div className="flex shrink-0 items-center [&_button]:rounded-md [&_button]:px-2 [&_button]:py-1 [&_button]:text-xs [&_button]:font-semibold [&_button]:uppercase [&_button]:tracking-wide [&_button]:text-white/90 [&_button:hover]:bg-white/10">
                {snackbar.action}
              </div>
            ) : null}
            <button
              type="button"
              onClick={(e) => handleClose(e, 'close')}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lg leading-none text-white/90 transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
              aria-label={t('common.dismiss')}
            >
              ×
            </button>
          </div>
        </div>
        </Fragment>
      </Suspense>
      </StoreModeContext.Provider>
    </DataContext.Provider>
  );
}

export default App;
